// services/benchmarkSampleData.js
// ─────────────────────────────────────────────────────────────────────────────
// Sample close-out data shipped inside the downloadable Excel template.
//
// This is not filler. The set is built so that the intelligence engine has
// something real to work with the moment it is uploaded:
//   • Commodity lines repeat across 2025 and 2026 with identical unit of
//     measure and currency, so matched-model inflation can be computed.
//   • NGN lines inflate ~18–23% a year and USD lines ~4–6%, which is what
//     naira depreciation actually did to Nigerian oil and gas procurement.
//   • Two lines deflate (mud engineering, VSAT bandwidth) so the engine has
//     to handle falling prices, not just rising ones.
//   • Diesel supply carries four awards across three suppliers — enough for a
//     genuine price-movement series and a competitive-tension read.
//   • Wellhead equipment is awarded three times to the same supplier, which is
//     what triggers the single-source exposure warning.
// ─────────────────────────────────────────────────────────────────────────────

const T30 = "Payment 30 days from receipt of undisputed invoice. Rates firm for the contract term.";
const T45 = "Payment 45 days net. 10% performance bond required on award. No advance payment.";
const T60 = "Payment 60 days net. 5% mobilisation on award, recovered pro-rata against invoices.";
const TMOB = "Payment 30 days. 15% mobilisation fee on award. Demobilisation at contractor cost.";

const J_LOW = "Lowest technically compliant bidder following commercial and technical evaluation.";
const J_2ND = "Second lowest bidder. Lowest bid disqualified at technical evaluation on HSE performance record.";
const J_INC = "Incumbent retained. Marginally above lowest bid but avoided mobilisation cost and demonstrated capacity for the full scope.";
const J_CAP = "Lowest bidder lacked equipment capacity for the full scope. Award made to next ranked bidder with verified fleet availability.";

const O_NGN = "Naira depreciation and higher diesel input costs drove roughly 20% uplift across all NGN-denominated service rates this cycle.";
const O_USD = "USD-denominated rates held broadly stable. International service providers absorbed most input cost pressure.";

export const SAMPLE_CLOSEOUTS = [
  // ── LOGISTICS ───────────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/LOG/2025/001", category: "Logistics", location: "Port Harcourt",
    commodityGroup: "Marine Vessel Charter", unitOfMeasure: "Per Day", currency: "NGN",
    serviceDescription: "Charter of 38m fast crew boat for personnel transfer between Onne Base and the OML 113 offshore facility.",
    supplierName: "Sea Trucks Group", benchmarkRate: 4200000, lowestRate: 3850000, awardedRate: 3950000,
    costSavingAchieved: 45000000, tenderDate: "2025-02-14", contractDuration: "12 months",
    commercialTerms: T45, awardJustification: J_2ND,
    marketObservation: "Vessel availability tightened in Q1 following increased offshore activity in the Eastern Niger Delta.",
  },
  {
    tenderRef: "LKL/SCM/LOG/2026/007", category: "Logistics", location: "Port Harcourt",
    commodityGroup: "Marine Vessel Charter", unitOfMeasure: "Per Day", currency: "NGN",
    serviceDescription: "Charter of 38m fast crew boat for personnel transfer between Onne Base and the OML 113 offshore facility.",
    supplierName: "Gegun Marine Services", benchmarkRate: 3950000, lowestRate: 4550000, awardedRate: 4680000,
    costSavingAchieved: 0, tenderDate: "2026-03-10", contractDuration: "12 months",
    commercialTerms: T45, awardJustification: J_LOW,
    marketObservation: "All four bids came in above the 2025 rate. Crew boat day rates are tracking marine diesel and dry-dock costs.",
  },
  {
    tenderRef: "LKL/SCM/LOG/2025/004", category: "Logistics", location: "Offshore OML 113",
    commodityGroup: "Helicopter Services", unitOfMeasure: "Per Flight Hour", currency: "USD",
    serviceDescription: "Provision of AW139 helicopter services for offshore crew change and medevac standby.",
    supplierName: "Caverton Helicopters", benchmarkRate: 5800, lowestRate: 5200, awardedRate: 5400,
    costSavingAchieved: 120000, tenderDate: "2025-04-22", contractDuration: "24 months",
    commercialTerms: T45, awardJustification: J_CAP, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/LOG/2026/011", category: "Logistics", location: "Offshore OML 113",
    commodityGroup: "Helicopter Services", unitOfMeasure: "Per Flight Hour", currency: "USD",
    serviceDescription: "Provision of AW139 helicopter services for offshore crew change and medevac standby.",
    supplierName: "Bristow Helicopters Nigeria", benchmarkRate: 5400, lowestRate: 5500, awardedRate: 5650,
    costSavingAchieved: 0, tenderDate: "2026-05-08", contractDuration: "24 months",
    commercialTerms: T45, awardJustification: J_LOW,
    marketObservation: "Only two operators hold current AOC for offshore passenger transfer. Limited competitive tension on this line.",
  },
  {
    tenderRef: "LKL/SCM/LOG/2025/009", category: "Logistics", location: "Port Harcourt",
    commodityGroup: "Onshore Haulage", unitOfMeasure: "Per Trip", currency: "NGN",
    serviceDescription: "Haulage of drilling consumables, casing and long lead items from Onne Port to the Otakikpo field location.",
    supplierName: "Gegun Logistics Limited", benchmarkRate: 1450000, lowestRate: 1280000, awardedRate: 1320000,
    costSavingAchieved: 15600000, tenderDate: "2025-06-05", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/LOG/2026/003", category: "Logistics", location: "Port Harcourt",
    commodityGroup: "Onshore Haulage", unitOfMeasure: "Per Trip", currency: "NGN",
    serviceDescription: "Haulage of drilling consumables, casing and long lead items from Onne Port to the Otakikpo field location.",
    supplierName: "Gegun Logistics Limited", benchmarkRate: 1320000, lowestRate: 1580000, awardedRate: 1610000,
    costSavingAchieved: 0, tenderDate: "2026-02-19", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "Haulage rates moved almost entirely with pump-price diesel. Road condition on the Ogoni axis added a surcharge.",
  },
  {
    tenderRef: "LKL/SCM/LOG/2025/014", category: "Logistics", location: "Port Harcourt",
    commodityGroup: "Warehousing and Yard Storage", unitOfMeasure: "Per SQM Per Month", currency: "NGN",
    serviceDescription: "Covered warehousing and open yard storage for long lead items, wellhead spares and drilling accessories.",
    supplierName: "SOWSCO Well Services", benchmarkRate: 9500, lowestRate: 8200, awardedRate: 8600,
    costSavingAchieved: 5400000, tenderDate: "2025-08-11", contractDuration: "18 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/LOG/2026/009", category: "Logistics", location: "Port Harcourt",
    commodityGroup: "Warehousing and Yard Storage", unitOfMeasure: "Per SQM Per Month", currency: "NGN",
    serviceDescription: "Covered warehousing and open yard storage for long lead items, wellhead spares and drilling accessories.",
    supplierName: "Shendian Yard Services", benchmarkRate: 8600, lowestRate: 10100, awardedRate: 10400,
    costSavingAchieved: 0, tenderDate: "2026-04-15", contractDuration: "18 months",
    commercialTerms: T30, awardJustification: J_LOW,
    marketObservation: "Relocation to Trans-Amadi reduced haulage distance, partly offsetting the higher storage rate.",
  },

  // ── WELL ENGINEERING ────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/WE/2025/002", category: "Well Engineering", location: "Offshore OPL 310",
    commodityGroup: "Directional Drilling Services", unitOfMeasure: "Per Day", currency: "USD",
    serviceDescription: "Provision of directional drilling with MWD and LWD services for the Ogo-2 appraisal well.",
    supplierName: "Baker Hughes Nigeria", benchmarkRate: 48000, lowestRate: 43500, awardedRate: 44800,
    costSavingAchieved: 224000, tenderDate: "2025-03-18", contractDuration: "6 months",
    commercialTerms: TMOB, awardJustification: J_2ND, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/WE/2026/005", category: "Well Engineering", location: "Offshore OPL 310",
    commodityGroup: "Directional Drilling Services", unitOfMeasure: "Per Day", currency: "USD",
    serviceDescription: "Provision of directional drilling with MWD and LWD services for the Ogo-2 appraisal well.",
    supplierName: "Schlumberger Nigeria", benchmarkRate: 44800, lowestRate: 46200, awardedRate: 47500,
    costSavingAchieved: 0, tenderDate: "2026-04-02", contractDuration: "6 months",
    commercialTerms: TMOB, awardJustification: J_LOW,
    marketObservation: "Rig activity across the Gulf of Guinea recovered through 2025, tightening directional service availability.",
  },
  {
    tenderRef: "LKL/SCM/WE/2025/006", category: "Well Engineering", location: "Port Harcourt",
    commodityGroup: "Wellhead Equipment Supply", unitOfMeasure: "Per Unit", currency: "USD",
    serviceDescription: "Supply of 13-5/8in 5000psi conventional wellhead and christmas tree assemblies with associated spares.",
    supplierName: "Bill Energy Services Limited", benchmarkRate: 385000, lowestRate: 352000, awardedRate: 361000,
    costSavingAchieved: 96000, tenderDate: "2025-05-27", contractDuration: "One-off",
    commercialTerms: T60, awardJustification: J_LOW, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/WE/2025/013", category: "Well Engineering", location: "Port Harcourt",
    commodityGroup: "Wellhead Equipment Supply", unitOfMeasure: "Per Unit", currency: "USD",
    serviceDescription: "Supply of 13-5/8in 5000psi conventional wellhead and christmas tree assemblies with associated spares.",
    supplierName: "Bill Energy Services Limited", benchmarkRate: 361000, lowestRate: 368000, awardedRate: 372000,
    costSavingAchieved: 0, tenderDate: "2025-11-19", contractDuration: "One-off",
    commercialTerms: T60, awardJustification: J_INC,
    marketObservation: "Only one bidder held API 6A certification with local stock. Sole-source exposure noted for escalation to category management.",
  },
  {
    tenderRef: "LKL/SCM/WE/2026/012", category: "Well Engineering", location: "Port Harcourt",
    commodityGroup: "Wellhead Equipment Supply", unitOfMeasure: "Per Unit", currency: "USD",
    serviceDescription: "Supply of 13-5/8in 5000psi conventional wellhead and christmas tree assemblies with associated spares.",
    supplierName: "Bill Energy Services Limited", benchmarkRate: 372000, lowestRate: 374000, awardedRate: 381000,
    costSavingAchieved: 0, tenderDate: "2026-06-11", contractDuration: "One-off",
    commercialTerms: T60, awardJustification: J_INC,
    marketObservation: "Third consecutive award to the same supplier. Bidder list must be widened before the next campaign.",
  },
  {
    tenderRef: "LKL/SCM/WE/2025/011", category: "Well Engineering", location: "Offshore OPL 310",
    commodityGroup: "Mud Engineering Services", unitOfMeasure: "Per Day", currency: "USD",
    serviceDescription: "Drilling fluids engineering, mud supply and solids control services for the OPL 310 drilling campaign.",
    supplierName: "MI SWACO Nigeria", benchmarkRate: 18500, lowestRate: 16800, awardedRate: 17200,
    costSavingAchieved: 78000, tenderDate: "2025-09-09", contractDuration: "4 months",
    commercialTerms: TMOB, awardJustification: J_2ND, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/WE/2026/002", category: "Well Engineering", location: "Offshore OPL 310",
    commodityGroup: "Mud Engineering Services", unitOfMeasure: "Per Day", currency: "USD",
    serviceDescription: "Drilling fluids engineering, mud supply and solids control services for the OPL 310 drilling campaign.",
    supplierName: "Newpark Drilling Fluids", benchmarkRate: 17200, lowestRate: 16400, awardedRate: 16900,
    costSavingAchieved: 36000, tenderDate: "2026-01-28", contractDuration: "4 months",
    commercialTerms: TMOB, awardJustification: J_2ND,
    marketObservation: "New entrant to the Nigerian market bid aggressively to establish a reference. Rate came in below the 2025 award.",
  },

  // ── FUEL SUPPLY ─────────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/FUEL/2025/001", category: "Fuel Supply", location: "Lagos",
    commodityGroup: "AGO Diesel Supply", unitOfMeasure: "Per Litre", currency: "NGN",
    serviceDescription: "Supply and delivery of Automotive Gas Oil to the Lagos head office and Lekki operational base.",
    supplierName: "Rainoil Limited", benchmarkRate: 1150, lowestRate: 1090, awardedRate: 1110,
    costSavingAchieved: 4800000, tenderDate: "2025-01-20", contractDuration: "6 months",
    commercialTerms: T30, awardJustification: J_2ND,
    marketObservation: "Depot prices eased slightly in January following improved local refining output.",
  },
  {
    tenderRef: "LKL/SCM/FUEL/2025/008", category: "Fuel Supply", location: "Lagos",
    commodityGroup: "AGO Diesel Supply", unitOfMeasure: "Per Litre", currency: "NGN",
    serviceDescription: "Supply and delivery of Automotive Gas Oil to the Lagos head office and Lekki operational base.",
    supplierName: "Ardova Plc", benchmarkRate: 1110, lowestRate: 1225, awardedRate: 1240,
    costSavingAchieved: 0, tenderDate: "2025-07-14", contractDuration: "6 months",
    commercialTerms: T30, awardJustification: J_LOW,
    marketObservation: "Mid-year FX adjustment pushed landed cost up sharply. All bids repriced within two weeks of submission.",
  },
  {
    tenderRef: "LKL/SCM/FUEL/2026/001", category: "Fuel Supply", location: "Lagos",
    commodityGroup: "AGO Diesel Supply", unitOfMeasure: "Per Litre", currency: "NGN",
    serviceDescription: "Supply and delivery of Automotive Gas Oil to the Lagos head office and Lekki operational base.",
    supplierName: "Rainoil Limited", benchmarkRate: 1240, lowestRate: 1398, awardedRate: 1415,
    costSavingAchieved: 0, tenderDate: "2026-02-06", contractDuration: "6 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "Diesel has risen every tender cycle since Q1 2025. Recommend shortening the term to 3 months to avoid locking in peaks.",
  },
  {
    tenderRef: "LKL/SCM/FUEL/2026/008", category: "Fuel Supply", location: "Lagos",
    commodityGroup: "AGO Diesel Supply", unitOfMeasure: "Per Litre", currency: "NGN",
    serviceDescription: "Supply and delivery of Automotive Gas Oil to the Lagos head office and Lekki operational base.",
    supplierName: "TotalEnergies Marketing Nigeria", benchmarkRate: 1415, lowestRate: 1462, awardedRate: 1480,
    costSavingAchieved: 0, tenderDate: "2026-06-23", contractDuration: "6 months",
    commercialTerms: T30, awardJustification: J_LOW,
    marketObservation: "Rate of increase is slowing. Three suppliers now bidding competitively on this line, which is holding the premium down.",
  },

  // ── OPERATIONS ──────────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/OPS/2025/003", category: "Operations", location: "Otakikpo",
    commodityGroup: "Production Chemicals - Demulsifier", unitOfMeasure: "Per Drum", currency: "NGN",
    serviceDescription: "Supply of crude oil demulsifier for the Otakikpo production facility, including on-site technical support.",
    supplierName: "Nalco Champion Nigeria", benchmarkRate: 2850000, lowestRate: 2610000, awardedRate: 2680000,
    costSavingAchieved: 20400000, tenderDate: "2025-04-09", contractDuration: "12 months",
    commercialTerms: T45, awardJustification: J_2ND,
    marketObservation: "Bottle testing confirmed the awarded product outperformed the cheaper alternative on water drop-out.",
  },
  {
    tenderRef: "LKL/SCM/OPS/2026/006", category: "Operations", location: "Otakikpo",
    commodityGroup: "Production Chemicals - Demulsifier", unitOfMeasure: "Per Drum", currency: "NGN",
    serviceDescription: "Supply of crude oil demulsifier for the Otakikpo production facility, including on-site technical support.",
    supplierName: "Baker Hughes Nigeria", benchmarkRate: 2680000, lowestRate: 3180000, awardedRate: 3240000,
    costSavingAchieved: 0, tenderDate: "2026-03-25", contractDuration: "12 months",
    commercialTerms: T45, awardJustification: J_2ND,
    marketObservation: "Imported chemical costs are FX-linked. Consider a longer term with a fixed FX rate to reduce exposure.",
  },
  {
    tenderRef: "LKL/SCM/OPS/2025/012", category: "Operations", location: "Otakikpo",
    commodityGroup: "Valve Maintenance and Testing", unitOfMeasure: "Per Unit", currency: "NGN",
    serviceDescription: "Overhaul, pressure testing and recertification of production manifold and wellhead valves.",
    supplierName: "Dorman Long Engineering", benchmarkRate: 1880000, lowestRate: 1690000, awardedRate: 1740000,
    costSavingAchieved: 8400000, tenderDate: "2025-10-16", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/OPS/2026/010", category: "Operations", location: "Otakikpo",
    commodityGroup: "Valve Maintenance and Testing", unitOfMeasure: "Per Unit", currency: "NGN",
    serviceDescription: "Overhaul, pressure testing and recertification of production manifold and wellhead valves.",
    supplierName: "Dorman Long Engineering", benchmarkRate: 1740000, lowestRate: 2020000, awardedRate: 2080000,
    costSavingAchieved: 0, tenderDate: "2026-05-19", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "Workshop labour and imported seal kits both repriced. Incumbent held rate 3% below the next bidder.",
  },

  // ── EPC / PROJECTS ──────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/EPC/2025/005", category: "EPC / Projects", location: "Otakikpo",
    commodityGroup: "Pipeline Construction", unitOfMeasure: "Per KM", currency: "USD",
    serviceDescription: "EPC of 6-inch by 12km crude evacuation flowline from Otakikpo to the export terminal tie-in point.",
    supplierName: "Nestoil Plc", benchmarkRate: 1240000, lowestRate: 1145000, awardedRate: 1180000,
    costSavingAchieved: 720000, tenderDate: "2025-05-30", contractDuration: "14 months",
    commercialTerms: T60, awardJustification: J_CAP, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/EPC/2026/004", category: "EPC / Projects", location: "Otakikpo",
    commodityGroup: "Pipeline Construction", unitOfMeasure: "Per KM", currency: "USD",
    serviceDescription: "EPC of 6-inch by 12km crude evacuation flowline from Otakikpo to the export terminal tie-in point.",
    supplierName: "Oilserv Limited", benchmarkRate: 1180000, lowestRate: 1215000, awardedRate: 1248000,
    costSavingAchieved: 0, tenderDate: "2026-07-08", contractDuration: "14 months",
    commercialTerms: T60, awardJustification: J_LOW,
    marketObservation: "Line pipe prices firmed on international steel. Right-of-way compensation also rose against the 2025 baseline.",
  },
  {
    tenderRef: "LKL/SCM/EPC/2025/010", category: "EPC / Projects", location: "Otakikpo",
    commodityGroup: "Storage Tank Fabrication", unitOfMeasure: "Lumpsum", currency: "NGN",
    serviceDescription: "Fabrication, delivery and installation of two 10,000 barrel crude oil storage tanks with associated pipework.",
    supplierName: "Dorman Long Engineering", benchmarkRate: 3450000000, lowestRate: 3180000000, awardedRate: 3265000000,
    costSavingAchieved: 185000000, tenderDate: "2025-11-12", contractDuration: "10 months",
    commercialTerms: T60, awardJustification: J_2ND,
    marketObservation: "Local fabrication yards had spare capacity, which held the price below the imported alternative.",
  },

  // ── IT ──────────────────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/IT/2025/002", category: "IT", location: "Lagos",
    commodityGroup: "VSAT Bandwidth", unitOfMeasure: "Per Mbps Per Month", currency: "USD",
    serviceDescription: "Provision of dedicated VSAT bandwidth for the Otakikpo field location and offshore facilities.",
    supplierName: "IPNX Nigeria Limited", benchmarkRate: 1450, lowestRate: 1280, awardedRate: 1320,
    costSavingAchieved: 18720, tenderDate: "2025-02-28", contractDuration: "24 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/IT/2026/002", category: "IT", location: "Lagos",
    commodityGroup: "VSAT Bandwidth", unitOfMeasure: "Per Mbps Per Month", currency: "USD",
    serviceDescription: "Provision of dedicated VSAT bandwidth for the Otakikpo field location and offshore facilities.",
    supplierName: "Suburban Telecom", benchmarkRate: 1320, lowestRate: 1205, awardedRate: 1255,
    costSavingAchieved: 11700, tenderDate: "2026-02-11", contractDuration: "24 months",
    commercialTerms: T30, awardJustification: J_2ND,
    marketObservation: "New LEO satellite capacity entering the West African market is pushing bandwidth prices down. Re-tender annually while this holds.",
  },
  {
    tenderRef: "LKL/SCM/IT/2025/007", category: "IT", location: "Lagos",
    commodityGroup: "Laptop Supply - Business Class", unitOfMeasure: "Per Unit", currency: "NGN",
    serviceDescription: "Supply of business-class notebook computers with three-year on-site warranty for SCM and Finance departments.",
    supplierName: "Zinox Technologies", benchmarkRate: 2150000, lowestRate: 1940000, awardedRate: 1995000,
    costSavingAchieved: 6200000, tenderDate: "2025-09-22", contractDuration: "One-off",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/IT/2026/005", category: "IT", location: "Lagos",
    commodityGroup: "Laptop Supply - Business Class", unitOfMeasure: "Per Unit", currency: "NGN",
    serviceDescription: "Supply of business-class notebook computers with three-year on-site warranty for SCM and Finance departments.",
    supplierName: "Yudala Business Solutions", benchmarkRate: 1995000, lowestRate: 2310000, awardedRate: 2365000,
    costSavingAchieved: 0, tenderDate: "2026-04-30", contractDuration: "One-off",
    commercialTerms: T30, awardJustification: J_LOW,
    marketObservation: "Hardware is fully import-dependent and tracks the naira almost one for one. Bulk-buying earlier in the year would reduce exposure.",
  },

  // ── TECHNICAL CONSULTANCY ───────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/TC/2025/004", category: "Technical Consultancy", location: "Lagos",
    commodityGroup: "Reservoir Engineering Study", unitOfMeasure: "Lumpsum", currency: "USD",
    serviceDescription: "Independent reservoir simulation and competent persons reserves certification for OPL 310.",
    supplierName: "RPS Energy Consultants", benchmarkRate: 680000, lowestRate: 615000, awardedRate: 635000,
    costSavingAchieved: 45000, tenderDate: "2025-06-18", contractDuration: "5 months",
    commercialTerms: T45, awardJustification: J_2ND,
    marketObservation: "Priced on a milestone basis. Independent certification is required by lenders, so scope is not discretionary.",
  },
  {
    tenderRef: "LKL/SCM/TC/2025/009", category: "Technical Consultancy", location: "Otakikpo",
    commodityGroup: "HAZOP Facilitation", unitOfMeasure: "Per Day", currency: "USD",
    serviceDescription: "Facilitation of HAZOP and SIL assessment workshops for the Otakikpo production expansion project.",
    supplierName: "DNV Nigeria", benchmarkRate: 4800, lowestRate: 4200, awardedRate: 4350,
    costSavingAchieved: 13500, tenderDate: "2025-08-05", contractDuration: "3 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_USD,
  },
  {
    tenderRef: "LKL/SCM/TC/2026/007", category: "Technical Consultancy", location: "Otakikpo",
    commodityGroup: "HAZOP Facilitation", unitOfMeasure: "Per Day", currency: "USD",
    serviceDescription: "Facilitation of HAZOP and SIL assessment workshops for the Otakikpo production expansion project.",
    supplierName: "Lloyd's Register Nigeria", benchmarkRate: 4350, lowestRate: 4480, awardedRate: 4620,
    costSavingAchieved: 0, tenderDate: "2026-05-14", contractDuration: "3 months",
    commercialTerms: T30, awardJustification: J_LOW,
    marketObservation: "Facilitator day rates are tightly clustered across the three certification bodies. Little room on price; negotiate on workshop duration instead.",
  },

  // ── CATERING ────────────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/CAT/2025/001", category: "Catering", location: "Offshore OML 113",
    commodityGroup: "Offshore Catering Services", unitOfMeasure: "Per Man-Day", currency: "NGN",
    serviceDescription: "Catering, housekeeping and laundry services for the 42-man offshore production facility.",
    supplierName: "Ferdinand Catering Services", benchmarkRate: 42500, lowestRate: 38200, awardedRate: 39400,
    costSavingAchieved: 47600000, tenderDate: "2025-03-04", contractDuration: "24 months",
    commercialTerms: T45, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/CAT/2026/003", category: "Catering", location: "Offshore OML 113",
    commodityGroup: "Offshore Catering Services", unitOfMeasure: "Per Man-Day", currency: "NGN",
    serviceDescription: "Catering, housekeeping and laundry services for the 42-man offshore production facility.",
    supplierName: "Newrest Nigeria", benchmarkRate: 39400, lowestRate: 46800, awardedRate: 47900,
    costSavingAchieved: 0, tenderDate: "2026-03-17", contractDuration: "24 months",
    commercialTerms: T45, awardJustification: J_LOW,
    marketObservation: "Food inflation ran ahead of headline CPI through 2025. Man-day rates rose across every bid received.",
  },
  {
    tenderRef: "LKL/SCM/CAT/2025/006", category: "Catering", location: "Otakikpo",
    commodityGroup: "Onshore Camp Catering", unitOfMeasure: "Per Man-Day", currency: "NGN",
    serviceDescription: "Catering and camp management services for the 68-man Otakikpo onshore operations camp.",
    supplierName: "Ferdinand Catering Services", benchmarkRate: 28600, lowestRate: 25400, awardedRate: 26300,
    costSavingAchieved: 57100000, tenderDate: "2025-07-29", contractDuration: "24 months",
    commercialTerms: T45, awardJustification: J_INC, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/CAT/2026/008", category: "Catering", location: "Otakikpo",
    commodityGroup: "Onshore Camp Catering", unitOfMeasure: "Per Man-Day", currency: "NGN",
    serviceDescription: "Catering and camp management services for the 68-man Otakikpo onshore operations camp.",
    supplierName: "Ferdinand Catering Services", benchmarkRate: 26300, lowestRate: 31200, awardedRate: 31900,
    costSavingAchieved: 0, tenderDate: "2026-06-02", contractDuration: "24 months",
    commercialTerms: T45, awardJustification: J_INC,
    marketObservation: "Local sourcing of produce held the onshore rate roughly 33% below the offshore equivalent.",
  },

  // ── SECURITY ────────────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/SEC/2025/002", category: "Security", location: "Port Harcourt",
    commodityGroup: "Static Guard Services", unitOfMeasure: "Per Guard Per Month", currency: "NGN",
    serviceDescription: "Provision of unarmed static guards for the Port Harcourt operational base, warehouse and yard.",
    supplierName: "Halogen Security Company", benchmarkRate: 385000, lowestRate: 342000, awardedRate: 355000,
    costSavingAchieved: 10800000, tenderDate: "2025-05-13", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/SEC/2026/004", category: "Security", location: "Port Harcourt",
    commodityGroup: "Static Guard Services", unitOfMeasure: "Per Guard Per Month", currency: "NGN",
    serviceDescription: "Provision of unarmed static guards for the Port Harcourt operational base, warehouse and yard.",
    supplierName: "Outsource Nigeria Limited", benchmarkRate: 355000, lowestRate: 428000, awardedRate: 437000,
    costSavingAchieved: 0, tenderDate: "2026-04-21", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_LOW,
    marketObservation: "The revised national minimum wage flowed directly into guarding rates. Little scope to negotiate below the statutory floor.",
  },
  {
    tenderRef: "LKL/SCM/SEC/2025/008", category: "Security", location: "Offshore OML 113",
    commodityGroup: "Marine Security Escort", unitOfMeasure: "Per Day", currency: "NGN",
    serviceDescription: "Armed naval escort and security vessel support for offshore marine and crew transfer operations.",
    supplierName: "OMS Marine Security", benchmarkRate: 8900000, lowestRate: 8150000, awardedRate: 8400000,
    costSavingAchieved: 45600000, tenderDate: "2025-10-07", contractDuration: "12 months",
    commercialTerms: T45, awardJustification: J_CAP,
    marketObservation: "Rate includes NN government-approved escort detail. Provider selection is constrained by naval accreditation.",
  },

  // ── MEDICAL SERVICES ────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/MED/2025/003", category: "Medical Services", location: "Offshore OML 113",
    commodityGroup: "Offshore Medic Deployment", unitOfMeasure: "Per Man-Month", currency: "NGN",
    serviceDescription: "Deployment of certified offshore medics with sick-bay management and medical consumables.",
    supplierName: "Flying Doctors Nigeria", benchmarkRate: 4850000, lowestRate: 4320000, awardedRate: 4460000,
    costSavingAchieved: 9360000, tenderDate: "2025-04-16", contractDuration: "18 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/MED/2026/006", category: "Medical Services", location: "Offshore OML 113",
    commodityGroup: "Offshore Medic Deployment", unitOfMeasure: "Per Man-Month", currency: "NGN",
    serviceDescription: "Deployment of certified offshore medics with sick-bay management and medical consumables.",
    supplierName: "Flying Doctors Nigeria", benchmarkRate: 4460000, lowestRate: 5290000, awardedRate: 5380000,
    costSavingAchieved: 0, tenderDate: "2026-05-26", contractDuration: "18 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "Offshore-certified medics remain scarce. Retention allowances are the main driver of the increase.",
  },
  {
    tenderRef: "LKL/SCM/MED/2025/010", category: "Medical Services", location: "Lagos",
    commodityGroup: "Medevac Retainer", unitOfMeasure: "Per Month", currency: "USD",
    serviceDescription: "Air ambulance and medical evacuation retainer covering all Nigerian operational locations.",
    supplierName: "Flying Doctors Nigeria", benchmarkRate: 32000, lowestRate: 28500, awardedRate: 29400,
    costSavingAchieved: 31200, tenderDate: "2025-11-25", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_USD,
  },

  // ── PROFESSIONAL SERVICES ───────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/PS/2025/001", category: "Professional Services", location: "Lagos",
    commodityGroup: "External Audit Services", unitOfMeasure: "Lumpsum", currency: "NGN",
    serviceDescription: "Statutory audit of LEKOIL Nigeria financial statements for the 2024 financial year.",
    supplierName: "KPMG Professional Services", benchmarkRate: 78500000, lowestRate: 71200000, awardedRate: 73400000,
    costSavingAchieved: 5100000, tenderDate: "2025-01-15", contractDuration: "4 months",
    commercialTerms: T45, awardJustification: J_2ND,
    marketObservation: "Big four fees are tightly banded. Scope reduction, not rate negotiation, is where savings are available on this line.",
  },
  {
    tenderRef: "LKL/SCM/PS/2026/001", category: "Professional Services", location: "Lagos",
    commodityGroup: "External Audit Services", unitOfMeasure: "Lumpsum", currency: "NGN",
    serviceDescription: "Statutory audit of LEKOIL Nigeria financial statements for the 2025 financial year.",
    supplierName: "Deloitte & Touche Nigeria", benchmarkRate: 73400000, lowestRate: 86500000, awardedRate: 88200000,
    costSavingAchieved: 0, tenderDate: "2026-01-13", contractDuration: "4 months",
    commercialTerms: T45, awardJustification: J_LOW,
    marketObservation: "Audit fees rose across the market on expanded IFRS sustainability reporting requirements.",
  },

  // ── OFFICE FACILITIES ───────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/OF/2025/005", category: "Office Facilities", location: "Lagos",
    commodityGroup: "Office Cleaning Services", unitOfMeasure: "Per Month", currency: "NGN",
    serviceDescription: "Daily cleaning, janitorial and washroom services for the Victoria Island head office.",
    supplierName: "Sparklean Facility Services", benchmarkRate: 6800000, lowestRate: 6050000, awardedRate: 6250000,
    costSavingAchieved: 6600000, tenderDate: "2025-08-19", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/OF/2026/009", category: "Office Facilities", location: "Lagos",
    commodityGroup: "Office Cleaning Services", unitOfMeasure: "Per Month", currency: "NGN",
    serviceDescription: "Daily cleaning, janitorial and washroom services for the Victoria Island head office.",
    supplierName: "Sparklean Facility Services", benchmarkRate: 6250000, lowestRate: 7380000, awardedRate: 7520000,
    costSavingAchieved: 0, tenderDate: "2026-07-01", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "Minimum wage revision and consumables inflation. Incumbent absorbed the equipment replacement cost to hold the increase to 20%.",
  },
  {
    tenderRef: "LKL/SCM/OF/2025/011", category: "Office Facilities", location: "Lagos",
    commodityGroup: "Generator Maintenance - 500KVA", unitOfMeasure: "Per Service", currency: "NGN",
    serviceDescription: "Preventive maintenance and quarterly servicing of two 500KVA standby generators at head office.",
    supplierName: "Mikano International", benchmarkRate: 3450000, lowestRate: 3080000, awardedRate: 3180000,
    costSavingAchieved: 3240000, tenderDate: "2025-12-03", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "OEM service retained to preserve warranty cover. Independent workshops quoted 18% lower but would void the warranty.",
  },

  // ── EQUIPMENT RENTAL ────────────────────────────────────────────────────────
  {
    tenderRef: "LKL/SCM/ER/2025/004", category: "Equipment Rental", location: "Port Harcourt",
    commodityGroup: "Forklift Rental - 5 Tonne", unitOfMeasure: "Per Month", currency: "NGN",
    serviceDescription: "Rental of 5-tonne diesel forklifts with operators for Onne warehouse and yard operations.",
    supplierName: "AllTrade Equipment Nigeria", benchmarkRate: 4200000, lowestRate: 3760000, awardedRate: 3880000,
    costSavingAchieved: 7680000, tenderDate: "2025-06-24", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_2ND, marketObservation: O_NGN,
  },
  {
    tenderRef: "LKL/SCM/ER/2026/005", category: "Equipment Rental", location: "Port Harcourt",
    commodityGroup: "Forklift Rental - 5 Tonne", unitOfMeasure: "Per Month", currency: "NGN",
    serviceDescription: "Rental of 5-tonne diesel forklifts with operators for Onne warehouse and yard operations.",
    supplierName: "AllTrade Equipment Nigeria", benchmarkRate: 3880000, lowestRate: 4560000, awardedRate: 4690000,
    costSavingAchieved: 0, tenderDate: "2026-06-16", contractDuration: "12 months",
    commercialTerms: T30, awardJustification: J_INC,
    marketObservation: "Rental rates track spare parts import cost and operator wages. Purchase versus rent analysis is worth revisiting at this level.",
  },
  {
    tenderRef: "LKL/SCM/ER/2025/009", category: "Equipment Rental", location: "Otakikpo",
    commodityGroup: "Portable Cabin Rental", unitOfMeasure: "Per Unit Per Month", currency: "NGN",
    serviceDescription: "Rental of portable office and accommodation cabins for the Otakikpo project site, including installation.",
    supplierName: "Setraco Nigeria Limited", benchmarkRate: 2850000, lowestRate: 2540000, awardedRate: 2620000,
    costSavingAchieved: 8280000, tenderDate: "2025-09-30", contractDuration: "18 months",
    commercialTerms: T45, awardJustification: J_2ND,
    marketObservation: "Long duration secured a 12% discount against the standard monthly rate card.",
  },
];
